import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import CountUp from "react-countup";
import {
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Alert,
    Tag,
    Divider,
    Spin,
    Empty,
    Button,
    List,
    Typography,
    Select,
    message,
    Dropdown,
    Menu,
    Input,
    Space,
    Descriptions,
    Modal
} from "antd";
import {
    DollarOutlined,
    RiseOutlined,
    FallOutlined,
    UserOutlined,
    CarOutlined,
    WalletOutlined,
    BankOutlined,
    TrophyOutlined,
    ThunderboltOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    LineChartOutlined,
    PieChartOutlined,
    InfoCircleOutlined,
    SyncOutlined,
    SearchOutlined,
    ReloadOutlined,
    HistoryOutlined
} from "@ant-design/icons";
import { useNotificationContext } from "contexts/NotificationContext";
import {
    getFinancialDashboardApi,
    getBusinessIntelligenceApi,
    getCostAnalysisApi,
    getFinancialHealthApi,
    getForecastingApi, // Add forecasting API
    type FinancialPeriod,
    type IFinancialDashboardResponse,
    type IBusinessIntelligenceResponse,
    type ICostAnalysisResponse,
    type IFinancialHealthResponse,
    type IForecastingResponse, // Add type
} from "api/financialDashboard";

// Fallback to old API if new one doesn't exist
import {
    financeApi,
    tripsCasherNetChartApi,
} from "api/trips";
import { IFinanceData } from "models/trips";
import moment from "moment";

function ArrowDownIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M12 15L7 10H17L12 15Z" fill="currentColor" />
        </svg>
    );
}

export default function EnhancedFinances() {
    const { showError } = useNotificationContext();
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState<FinancialPeriod>("daily");
    const [selectedYear, setSelectedYear] = useState("This Year");

    // New comprehensive data
    const [dashboardData, setDashboardData] = useState<IFinancialDashboardResponse | null>(null);
    const [businessIntelligence, setBusinessIntelligence] = useState<IBusinessIntelligenceResponse | null>(null);
    const [costAnalysis, setCostAnalysis] = useState<ICostAnalysisResponse | null>(null);
    const [healthData, setHealthData] = useState<IFinancialHealthResponse | null>(null);
    const [forecastData, setForecastData] = useState<IForecastingResponse | null>(null); // Add forecast state

    // Simulation mode for demoing the enhanced UI
    const [isSimulated, setIsSimulated] = useState(false);

    // Fallback old data
    const [oldFinanceData, setOldFinanceData] = useState<IFinanceData | null>(null);

    const [chartSeries, setChartSeries] = useState<{
        value: { name: string; data: number[] }[];
        label: string[];
    }>({ value: [], label: [] });

    useEffect(() => {
        fetchAllData();
        handleYearChange("this year");
    }, [selectedPeriod, isSimulated]); // eslint-disable-line

    const fetchAllData = async () => {
        if (isSimulated) {
            loadSimulationData();
            return;
        }

        setLoading(true);
        try {
            // Try new comprehensive API first with CACHING enabled
            const dashboardResponse = await getFinancialDashboardApi(selectedPeriod);

            if (dashboardResponse.data) {
                setDashboardData(dashboardResponse.data);

                // Fetch additional data
                // Note: These now support caching
                const [biResponse, costResponse, healthResponse, forecastResponse] = await Promise.all([
                    getBusinessIntelligenceApi(selectedPeriod, true),
                    getCostAnalysisApi(selectedPeriod, true),
                    getFinancialHealthApi(true),
                    getForecastingApi(30, true),
                ]);

                if (biResponse.data) setBusinessIntelligence(biResponse.data);
                if (costResponse.data) setCostAnalysis(costResponse.data);
                if (healthResponse.data) setHealthData(healthResponse.data);
                if (forecastResponse.data) setForecastData(forecastResponse.data);
            } else {
                // Fallback if data is null but no error thrown
                throw new Error("No data returned");
            }
        } catch (error) {
            // Fallback to old API with CACHING
            console.log("New API not available, using legacy finance API");
            const { data, error: oldError } = await financeApi(true);
            if (oldError) {
                showError(oldError.message);
            } else if (data) {
                setOldFinanceData({
                    total_income: data?.data.total_income ?? 0,
                    driver_income: data?.data.total_driver_income ?? 0,
                    total_gross: data?.data.total_gross_income ?? 0,
                    total_net: data?.data.total_net_income ?? 0,
                    total_tax: data?.data.total_tax ?? 0,
                    total_passengers: data?.data.total_passengers ?? 0,
                    total_drivers: data?.data.total_drivers ?? 0,
                    total_drivers_wallet_balance: data?.data.total_drivers_wallet_balance ?? 0,
                    total_driver_used_wallet_balance: data?.data.total_driver_used_wallet_balance ?? 0,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const loadSimulationData = () => {
        setLoading(true);
        // Simulate API delay
        setTimeout(() => {
            setDashboardData({
                period: { type: selectedPeriod, start_date: "2024-01-01", end_date: "2024-01-31" },
                revenue: {
                    total_revenue: 1250000.50,
                    platform_cut: 250000.10,
                    tax_collected: 187500.00,
                    driver_earnings: 812500.40,
                    penalty_revenue: 12500.00,
                    average_trip_revenue: 450.50,
                    trip_count: 2775
                },
                costs: {
                    total_costs: 45000.00,
                    system_costs: 25000.00,
                    penalty_costs: 5000.00,
                    cancellation_costs: 15000.00
                },
                trips: {
                    total_trips: 3200,
                    completed_trips: 2775,
                    cancelled_trips: 425,
                    completion_rate: 86.7,
                    economics: {
                        average_revenue_per_trip: 450.50,
                        cost_per_trip: 16.20,
                        profit_per_trip: 434.30,
                        profit_margin_per_trip: 96.4
                    }
                },
                drivers: {
                    active_drivers: 120,
                    total_earnings: 812500.40,
                    wallet_balances: 450000.00,
                    utilization_rate: 78.5
                },
                profitability: {
                    gross_profit: 437500.10,
                    net_profit: 392500.10,
                    gross_margin: 35.0,
                    net_margin: 31.4,
                    roi: 12.5,
                    break_even_point: 100000.00
                },
                trends: {
                    revenue_trend: {
                        direction: "up",
                        change_percent: 12.5,
                        period: "last_7_days"
                    },
                    cost_trend: {
                        direction: "stable",
                        change_percent: 2.1,
                        period: "last_7_days"
                    },
                    trip_trend: {
                        direction: "up",
                        change_percent: 8.3,
                        period: "last_7_days"
                    },
                    insights: [
                        "Revenue is trending upward with 12% growth this month",
                        "Trip completion rate could be improved with better driver incentives",
                        "High driver utilization at 78.5% during peak hours"
                    ]
                },
            });

            setBusinessIntelligence({
                kpis: [
                    { name: "Retention Rate", value: 92, target: 95, status: "good", trend: "up", description: "Monthly passenger retention" },
                    { name: "CAC", value: 45, target: 50, status: "good", trend: "down", description: "Customer Acquisition Cost" },
                    { name: "LTV", value: 1200, target: 1500, status: "warning", trend: "stable", description: "Customer Lifetime Value" }
                ],
                insights: ["High growth in suburb areas", "Peak demand between 7 AM and 9 AM"],
                recommendations: ["Increase driver incentives in North Zone", "Launch promo codes for new users"]
            });

            setCostAnalysis({
                total_costs: 45000.00,
                cost_breakdown: [
                    { category: "Infrastructure", amount: 25000, percentage: 55, trend: "stable", optimization_potential: 15, recommendations: ["Move to ARM instances"] },
                    { category: "Marketing", amount: 15000, percentage: 33, trend: "up", optimization_potential: 10, recommendations: ["Optimize ad spend"] },
                    { category: "Support", amount: 5000, percentage: 11, trend: "down", optimization_potential: 5, recommendations: ["Implement AI chatbot"] }
                ],
                optimization_opportunities: ["Consolidate database instances", "Automate driver background checks"],
                projected_savings: 8500.00
            });

            setHealthData({
                score: 88,
                status: "excellent",
                factors: [
                    { name: "Liquidity", score: 95, impact: "High" },
                    { name: "Profitability", score: 82, impact: "Medium" },
                    { name: "Operational Efficiency", score: 87, impact: "High" }
                ],
                recommendations: ["Maintain current cash reserves", "Focus on reducing cloud infrastructure costs"]
            });

            setForecastData({
                forecast_period: "Next 30 Days",
                predictions: Array.from({ length: 30 }).map((_, i) => ({
                    date: moment().add(i, 'days').format('YYYY-MM-DD'),
                    predicted_revenue: 40000 + Math.random() * 10000,
                    confidence_level: 0.85 + Math.random() * 0.1,
                    factors: { seasonal_trend: "Positive", day_of_week: "Varies", weather_impact: "Low" }
                })),
                accuracy_metrics: { mean_absolute_percentage_error: 4.5, r_squared: 0.92, last_trained: moment().subtract(1, 'day').toISOString() },
                assumptions: ["Stable fuel prices", "Continued driver growth", "Revenue expected to peak next weekend"],
            });

            setLoading(false);
        }, 800);
    };

    const handleYearChange = async (year: string) => {
        setSelectedYear(year);
        if (year.toLowerCase() === "this year") {
            year = new Date().getFullYear().toString();
        }

        if (isSimulated) {
            // Mock chart data for simulation
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            setChartSeries({
                value: [{
                    name: "Net Income",
                    data: months.map(() => 85000 + Math.random() * 50000)
                }],
                label: months
            });
            return;
        }

        const { data, error } = await tripsCasherNetChartApi(year, true);
        if (error) {
            showError(error.message);
        } else {
            setChartSeries({
                value: [
                    {
                        name: "Net Income",
                        data: Object.values<number>(data?.data.graph_data ?? {}).map(
                            (value) => Math.round((value + Number.EPSILON) * 100) / 100
                        ),
                    },
                ],
                label: Object.keys(data?.data.graph_data ?? {}).map((key) =>
                    moment(key).format("MMM D")
                ),
            });
        }
    };

    const yearItems = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [{ key: "this_year", label: "This Year" }];

        for (let year = currentYear; year >= 2020; year--) {
            years.push({ key: year.toString(), label: year.toString() });
        }

        return years;
    }, []);

    const getPeriodLabel = (p: string) => {
        return p.charAt(0).toUpperCase() + p.slice(1);
    };

    const periodItems = [
        { key: "daily", label: "Daily" },
        { key: "weekly", label: "Weekly" },
        { key: "monthly", label: "Monthly" },
        { key: "quarterly", label: "Quarterly" },
        { key: "yearly", label: "Yearly" },
    ];

    const getHealthColor = (score: number) => {
        if (score >= 80) return "#52c41a";
        if (score >= 60) return "#faad14";
        if (score >= 40) return "#ff7a45";
        return "#ff4d4f";
    };

    const getTrendIcon = (direction: "up" | "down" | "stable") => {
        if (direction === "up") return <RiseOutlined style={{ color: "#52c41a" }} />;
        if (direction === "down") return <FallOutlined style={{ color: "#ff4d4f" }} />;
        return <LineChartOutlined style={{ color: "#1890ff" }} />;
    };

    if (loading) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <Spin size="large" tip="Loading financial dashboard..." />
            </div>
        );
    }

    // Render new comprehensive dashboard if data is available
    if (dashboardData) {
        return (
            <div className="w-full space-y-6 p-6 bg-gray-50">
                {/* Header with Period Selector */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">💰 Financial Dashboard</h1>
                        <p className="text-gray-500 mt-2">
                            Complete business analytics and insights • {(dashboardData.period.type || selectedPeriod).toUpperCase()}
                        </p>
                    </div>
                    <Dropdown
                        overlay={
                            <Menu
                                items={periodItems.map((period) => ({
                                    key: period.key,
                                    label: (
                                        <span onClick={() => setSelectedPeriod(period.key as FinancialPeriod)}>
                                            {period.label}
                                        </span>
                                    ),
                                }))}
                            />
                        }
                    >
                        <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-all active:scale-95">
                            <ThunderboltOutlined className={isSimulated ? "text-amber-500" : "text-blue-500"} />
                            {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}
                            <ArrowDownIcon />
                        </button>
                    </Dropdown>

                    {isSimulated && (
                        <Tag color="orange" className="ml-4 px-3 py-1 text-sm font-semibold rounded-full border-none shadow-sm animate-pulse">
                            SIMULATED DATA
                        </Tag>
                    )}

                    <Button
                        type={isSimulated ? "default" : "primary"}
                        onClick={() => {
                            const next = !isSimulated;
                            setIsSimulated(next);
                            if (next) loadSimulationData();
                            else fetchAllData();
                        }}
                        className="ml-auto flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                    >
                        {isSimulated ? <CheckCircleOutlined /> : <LineChartOutlined />}
                        {isSimulated ? "Return to Live API" : "Preview Premium UI"}
                    </Button>
                </div>

                {/* Financial Health Score */}
                {healthData && (
                    <Card className="shadow-lg">
                        <Row gutter={[24, 24]} align="middle">
                            <Col xs={24} md={8}>
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold mb-4">Financial Health Score</h3>
                                    <Progress
                                        type="circle"
                                        percent={healthData.score}
                                        strokeColor={getHealthColor(healthData.score)}
                                        width={150}
                                        format={(percent) => (
                                            <div>
                                                <div className="text-3xl font-bold">{percent}</div>
                                                <div className="text-sm text-gray-500">{healthData.status.toUpperCase()}</div>
                                            </div>
                                        )}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} md={16}>
                                <div className="space-y-3">
                                    <h4 className="font-semibold">Key Health Factors:</h4>
                                    {healthData.factors?.map((factor, idx) => (
                                        <div key={idx} className="flex items-center justify-between">
                                            <span className="text-gray-700">{factor.name}</span>
                                            <div className="flex items-center gap-2">
                                                <Progress
                                                    percent={factor.score}
                                                    size="small"
                                                    style={{ width: 100 }}
                                                    strokeColor={getHealthColor(factor.score)}
                                                />
                                                <span className="text-sm text-gray-500">{factor.impact}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {healthData.recommendations && healthData.recommendations.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                            <p className="font-medium text-blue-600 mb-2">Recommendations:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {healthData.recommendations?.filter(r => r).map((rec, i) => (
                                                    <Tag key={i} color="blue" className="rounded-md">{rec}</Tag>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* Profitability Overview */}
                <Card className="shadow-lg" style={{ borderTop: "4px solid #52c41a" }}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={6}>
                            <Statistic
                                title={<span className="text-lg font-semibold">💰 Net Profit</span>}
                                value={dashboardData.profitability.net_profit}
                                precision={2}
                                valueStyle={{ color: "#52c41a", fontSize: 32, fontWeight: "bold" }}
                                prefix="Br "
                                suffix={getTrendIcon(dashboardData.trends.revenue_trend.direction)}
                            />
                            <p className="text-gray-500 text-sm mt-2">
                                {(dashboardData.profitability.net_margin || 0).toFixed(2)}% margin
                            </p>
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic
                                title={<span className="text-lg font-semibold">📊 Gross Profit</span>}
                                value={dashboardData.profitability.gross_profit}
                                precision={2}
                                valueStyle={{ color: "#1890ff", fontSize: 28 }}
                                prefix="Br "
                            />
                            <p className="text-gray-500 text-sm mt-2">
                                {(dashboardData.profitability.gross_margin || 0).toFixed(2)}% margin
                            </p>
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic
                                title={<span className="text-lg font-semibold">📈 ROI</span>}
                                value={dashboardData.profitability.roi}
                                precision={2}
                                valueStyle={{ color: "#faad14", fontSize: 28 }}
                                suffix="%"
                            />
                            <p className="text-gray-500 text-sm mt-2">Return on investment</p>
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic
                                title={<span className="text-lg font-semibold">⚖️ Break Even</span>}
                                value={dashboardData.profitability.break_even_point}
                                precision={2}
                                valueStyle={{ color: "#722ed1", fontSize: 28 }}
                                prefix="Br "
                            />
                            <p className="text-gray-500 text-sm mt-2">Per trip to break even</p>
                        </Col>
                    </Row>
                </Card>

                {/* Revenue & Costs */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Card title={<span className="text-xl font-bold">💵 Revenue Breakdown</span>} className="shadow-lg h-full">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                    <span className="font-semibold">Total Revenue</span>
                                    <span className="text-xl font-bold text-green-600">
                                        Br <CountUp end={dashboardData.revenue.total_revenue} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                    <span>Platform Cut</span>
                                    <span className="text-lg font-semibold text-blue-600">
                                        Br <CountUp end={dashboardData.revenue.platform_cut} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                                    <span>Tax Collected</span>
                                    <span className="text-lg font-semibold text-orange-600">
                                        Br <CountUp end={dashboardData.revenue.tax_collected} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                                    <span>Driver Earnings</span>
                                    <span className="text-lg font-semibold text-purple-600">
                                        Br <CountUp end={dashboardData.revenue.driver_earnings} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-rose-50 rounded-lg">
                                    <span>Penalty Revenue</span>
                                    <span className="text-lg font-semibold text-rose-600">
                                        Br <CountUp end={dashboardData.revenue.penalty_revenue || 0} separator="," decimals={2} />
                                    </span>
                                </div>
                                <Divider />
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Avg Revenue/Trip</span>
                                    <span className="font-semibold">
                                        Br {dashboardData.revenue.average_trip_revenue.toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Total Trips</span>
                                    <span className="font-semibold">
                                        <CountUp end={dashboardData.revenue.trip_count} separator="," />
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Card title={<span className="text-xl font-bold">💸 Cost Analysis</span>} className="shadow-lg h-full">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                    <span className="font-semibold">Total Costs</span>
                                    <span className="text-xl font-bold text-red-600">
                                        Br <CountUp end={dashboardData.costs.total_costs} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                    <span>System Operations</span>
                                    <span className="text-lg font-semibold text-gray-600">
                                        Br <CountUp end={dashboardData.costs.system_costs} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                                    <span>Penalty Costs</span>
                                    <span className="text-lg font-semibold text-yellow-600">
                                        Br <CountUp end={dashboardData.costs.penalty_costs} separator="," decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                                    <span>Cancellation Costs</span>
                                    <span className="text-lg font-semibold text-orange-600">
                                        Br <CountUp end={dashboardData.costs.cancellation_costs} separator="," decimals={2} />
                                    </span>
                                </div>
                                {costAnalysis && costAnalysis.projected_savings > 0 && (
                                    <>
                                        <Divider />
                                        <Alert
                                            message="Optimization Opportunity"
                                            description={`Potential savings: Br ${costAnalysis.projected_savings.toFixed(2)}`}
                                            type="success"
                                            showIcon
                                            icon={<ThunderboltOutlined />}
                                        />
                                    </>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Revenue & Cost Visualizations (Pie Charts) */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Card title={<span className="text-xl font-bold">🔘 Revenue Breakdown %</span>} className="shadow-lg">
                            <Chart
                                options={{
                                    labels: ["Platform Cut", "Tax Collected", "Driver Earnings"],
                                    colors: ["#1890ff", "#faad14", "#722ed1"],
                                    legend: { position: "bottom" },
                                }}
                                series={[
                                    dashboardData.revenue.platform_cut,
                                    dashboardData.revenue.tax_collected,
                                    dashboardData.revenue.driver_earnings,
                                ]}
                                type="donut"
                                height={300}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card title={<span className="text-xl font-bold">🔘 Cost Breakdown %</span>} className="shadow-lg">
                            <Chart
                                options={{
                                    labels: ["System Operations", "Penalty Costs", "Cancellation Costs"],
                                    colors: ["#8c8c8c", "#ffec3d", "#ff4d4f"],
                                    legend: { position: "bottom" },
                                }}
                                series={[
                                    dashboardData.costs.system_costs,
                                    dashboardData.costs.penalty_costs,
                                    dashboardData.costs.cancellation_costs,
                                ]}
                                type="donut"
                                height={300}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Stakeholder Analytics */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">👥 Stakeholder Analytics</h2>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card title={<span className="text-xl font-bold">🚕 Driver Performance</span>} className="shadow-lg h-full">
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                        <span className="font-semibold">Active Drivers</span>
                                        <span className="text-xl font-bold text-blue-600">
                                            <CountUp end={dashboardData.drivers.active_drivers} separator="," />
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center px-3">
                                        <span className="text-gray-600">Total Driver Earnings</span>
                                        <span className="font-semibold text-gray-800">
                                            Br {(dashboardData.drivers.total_earnings || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center px-3">
                                        <span className="text-gray-600">Utilization Rate</span>
                                        <span className="font-semibold text-blue-500">
                                            {(dashboardData.drivers.utilization_rate || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                    <Progress
                                        percent={dashboardData.drivers.utilization_rate || 0}
                                        status="active"
                                        strokeColor="#1890ff"
                                    />
                                    <div className="flex justify-between items-center px-3">
                                        <span className="text-gray-600">Avg Wallet Balance</span>
                                        <span className="font-semibold text-pink-600">
                                            Br {((dashboardData.drivers.wallet_balances || 0) / (dashboardData.drivers.active_drivers || 1)).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card title={<span className="text-xl font-bold">👤 Passenger Insights</span>} className="shadow-lg h-full">
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                                        <span className="font-semibold">Total Revenue Contributed</span>
                                        <span className="text-xl font-bold text-purple-600">
                                            Br {(dashboardData.revenue.total_revenue || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center px-3">
                                        <span className="text-gray-600">Average Fare per Trip</span>
                                        <span className="font-semibold text-gray-800">
                                            Br {(dashboardData.revenue.average_trip_revenue || 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center px-3">
                                        <span className="text-gray-600">Repeat Trip Likelihood</span>
                                        <span className="font-semibold text-purple-500">
                                            {(dashboardData.trips.completion_rate || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                    <Progress
                                        percent={dashboardData.trips.completion_rate || 0}
                                        strokeColor="#722ed1"
                                    />
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* Trip Economics */}
                <Card title={<span className="text-xl font-bold">🚗 Trip Economics</span>} className="shadow-lg">
                    <Row gutter={[16, 16]}>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="Total Trips"
                                value={dashboardData.trips.total_trips}
                                prefix={<CarOutlined />}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="Completed"
                                value={dashboardData.trips.completed_trips}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: "#52c41a" }}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="Cancelled"
                                value={dashboardData.trips.cancelled_trips}
                                prefix={<WarningOutlined />}
                                valueStyle={{ color: "#ff4d4f" }}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Statistic
                                title="Completion Rate"
                                value={dashboardData.trips.completion_rate}
                                precision={2}
                                suffix="%"
                                valueStyle={{
                                    color: (dashboardData.trips.completion_rate || 0) >= 85 ? "#52c41a" : "#faad14"
                                }}
                            />
                        </Col>
                    </Row>
                    <Divider />
                    <Row gutter={[16, 16]}>
                        <Col xs={12} md={6}>
                            <div className="text-center">
                                <p className="text-gray-500 text-sm">Avg Revenue/Trip</p>
                                <p className="text-2xl font-bold text-green-600">
                                    Br {(dashboardData.trips.economics.average_revenue_per_trip || 0).toFixed(2)}
                                </p>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="text-center">
                                <p className="text-gray-500 text-sm">Cost/Trip</p>
                                <p className="text-2xl font-bold text-red-600">
                                    Br {(dashboardData.trips.economics.cost_per_trip || 0).toFixed(2)}
                                </p>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="text-center">
                                <p className="text-gray-500 text-sm">Profit/Trip</p>
                                <p className="text-2xl font-bold text-blue-600">
                                    Br {(dashboardData.trips.economics.profit_per_trip || 0).toFixed(2)}
                                </p>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="text-center">
                                <p className="text-gray-500 text-sm">Profit Margin/Trip</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {(dashboardData.trips.economics.profit_margin_per_trip || 0).toFixed(2)}%
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Trends & Insights */}
                {dashboardData.trends.insights?.length ? (
                    <Card title={<span className="text-xl font-bold">💡 Business Insights</span>} className="shadow-lg">
                        <List
                            size="small"
                            dataSource={dashboardData.trends.insights}
                            renderItem={(item) => (
                                <List.Item className="border-none px-0 py-2">
                                    <div className="flex items-start gap-3">
                                        <div className="bg-blue-50 p-1 rounded-full mt-1">
                                            <InfoCircleOutlined className="text-blue-500 text-xs" />
                                        </div>
                                        <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                ) : (
                    <Card title={<span className="text-xl font-bold">💡 Business Insights</span>} className="shadow-lg">
                        <div className="text-center py-8 text-gray-400">
                            <InfoCircleOutlined className="text-2xl mb-2 opacity-20" />
                            <p>No automated insights available for this period</p>
                        </div>
                    </Card>
                )}

                {/* Business Intelligence */}
                {businessIntelligence && (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card title={<span className="text-xl font-bold">🎯 Key Performance Indicators</span>} className="shadow-lg">
                                <div className="space-y-4">
                                    {businessIntelligence.kpis?.map((kpi, idx) => (
                                        <div key={idx} className="border-b pb-3">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="font-semibold">{kpi.name}</span>
                                                <Tag color={kpi.status === "good" ? "green" : kpi.status === "warning" ? "orange" : "red"}>
                                                    {kpi.status.toUpperCase()}
                                                </Tag>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <Progress
                                                    percent={(kpi.value / kpi.target) * 100}
                                                    strokeColor={kpi.status === "good" ? "#52c41a" : "#faad14"}
                                                    style={{ flex: 1, marginRight: 16 }}
                                                />
                                                <span className="text-sm text-gray-500">
                                                    {kpi.value.toLocaleString()} / {kpi.target.toLocaleString()}
                                                </span>
                                            </div>
                                            <p className="description m-0">{getPeriodLabel(dashboardData.period.type || selectedPeriod)}</p>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card title={<span className="text-xl font-bold">🚀 Recommendations</span>} className="shadow-lg">
                                <div className="space-y-3">
                                    {businessIntelligence.recommendations?.map((rec, idx) => (
                                        <Alert
                                            key={idx}
                                            message={rec}
                                            type="success"
                                            showIcon
                                            icon={<ThunderboltOutlined />}
                                        />
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Finance Graph */}
                <Card className="shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold">📈 Net Income Trend</h3>
                        <Dropdown
                            overlay={
                                <Menu
                                    items={yearItems.map((year) => ({
                                        key: year.key,
                                        label: (
                                            <span onClick={() => handleYearChange(year.label)}>
                                                {year.label}
                                            </span>
                                        ),
                                    }))}
                                />
                            }
                        >
                            <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50">
                                {selectedYear}
                                <ArrowDownIcon />
                            </button>
                        </Dropdown>
                    </div>
                    <Chart
                        options={{
                            chart: {
                                id: "finances-chart",
                                toolbar: { show: false },
                            },
                            xaxis: { categories: chartSeries.label },
                            yaxis: {
                                labels: {
                                    formatter: (value: number) => `Br ${value.toFixed(2)}`,
                                },
                            },
                            stroke: { curve: "smooth", width: 3 },
                            markers: {
                                size: 5,
                                colors: ["#52c41a"],
                                strokeColors: "#ffffff",
                                strokeWidth: 2,
                                hover: { size: 7 },
                            },
                            fill: { type: "gradient" },
                            tooltip: { theme: "dark" },
                            dataLabels: { enabled: false },
                        }}
                        series={chartSeries.value}
                        type="line"
                        height={400}
                    />
                </Card>

                {/* Forecasting Chart */}
                {forecastData && (
                    <Card title={<span className="text-2xl font-bold">🔮 Revenue Forecasting ({forecastData.forecast_period || "30 Days"})</span>} className="shadow-lg">
                        <Chart
                            options={{
                                chart: {
                                    id: "forecasting-chart",
                                    toolbar: { show: false },
                                },
                                xaxis: {
                                    categories: (forecastData.predictions || []).map(f => moment(f.date).format("MMM D")),
                                    title: { text: "Forecast Period" }
                                },
                                yaxis: {
                                    labels: {
                                        formatter: (value: number) => `Br ${value.toFixed(0)}`,
                                    },
                                },
                                stroke: { curve: "smooth", width: [3, 0] },
                                colors: ["#1890ff", "#1890ff33"],
                                fill: {
                                    type: ["solid", "solid"],
                                    opacity: [1, 0.2]
                                },
                                tooltip: {
                                    theme: "dark",
                                    shared: true,
                                },
                                dataLabels: { enabled: false },
                                legend: { show: true }
                            }}
                            series={[
                                {
                                    name: "Predicted Revenue",
                                    type: "line",
                                    data: (forecastData.predictions || []).map(f => f.predicted_revenue)
                                },
                                {
                                    name: "Confidence Level",
                                    type: "area",
                                    data: (forecastData.predictions || []).map(f => f.confidence_level * 100)
                                }
                            ]}
                            type="line"
                            height={400}
                        />
                        {(forecastData.assumptions?.length || 0) > 0 && (
                            <div className="mt-6 space-y-2">
                                <h4 className="font-semibold text-gray-700">Forecast Assumptions & Insights:</h4>
                                {forecastData.assumptions?.map((assumption, idx) => (
                                    <Tag key={idx} color="blue" className="p-1 mb-2">
                                        ✨ {assumption}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                )}
            </div>
        );
    }

    // Fallback to old UI if new API not available
    return (
        <div className="w-full p-8 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <Card className="shadow-xl border-none rounded-2xl overflow-hidden">
                    <div className="bg-amber-50 p-6 flex items-start gap-4">
                        <div className="p-3 bg-amber-100 rounded-xl text-amber-600 shrink-0">
                            <WarningOutlined style={{ fontSize: '24px' }} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">Legacy Finance View</h2>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                The new comprehensive financial dashboard API is currently being deployed.
                                We are showing basic finance data collected from legacy endpoints in the meantime.
                            </p>

                            <div className="flex flex-wrap gap-3">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ThunderboltOutlined />}
                                    onClick={() => {
                                        setIsSimulated(true);
                                        loadSimulationData();
                                    }}
                                    className="bg-gray-800 hover:bg-black border-none h-12 px-6 rounded-xl font-semibold shadow-lg shadow-gray-200"
                                >
                                    Preview Enhanced Dashboard (Simulation)
                                </Button>
                                <Button
                                    size="large"
                                    onClick={() => fetchAllData()}
                                    className="h-12 px-6 rounded-xl font-medium border-gray-200"
                                >
                                    Refresh Live Data
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            <Divider className="my-10">Live Legacy Data</Divider>

            {oldFinanceData ? (
                <div className="space-y-6">
                    <Card>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title="Total Income"
                                    value={oldFinanceData.total_income}
                                    precision={2}
                                    prefix="Br "
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title="Net Income"
                                    value={oldFinanceData.total_net}
                                    precision={2}
                                    prefix="Br "
                                    valueStyle={{ color: "#52c41a" }}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title="Driver Income"
                                    value={oldFinanceData.driver_income}
                                    precision={2}
                                    prefix="Br "
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title="Total Tax"
                                    value={oldFinanceData.total_tax}
                                    precision={2}
                                    prefix="Br "
                                />
                            </Col>
                        </Row>
                    </Card>
                </div>
            ) : (
                <Empty description="No financial data available" />
            )}
        </div>
    );
}
